'use client';

import HeaderLogo from '@components/header-logo/HeaderLogo';
import ROUTES from '@lib/constants/routes';
import { handleOpenMenuMobile, handleScrollVisible, handleVisibleHeader, headerColorSchemeSelector, isLoadingSelector, scrollUpdateSelector, visibleHeaderSelector } from '@lib/store/slices/ComponentsSlice';
import { SCHEMA } from '@lib/tools/helpers/constants';
import { animated, useScroll, useSpring } from '@react-spring/web';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocomotiveScroll } from 'react-locomotive-scroll';
import { useDispatch, useSelector } from 'react-redux';
import style from './Header.module.scss';
import HeaderBar from './header-bar/HeaderBar';
function Header({
  isMobile,
  deviceType
}) {
  const dispatch = useDispatch();
  const pathname = usePathname();
  const headerColorScheme = useSelector(headerColorSchemeSelector);
  const isLoading = useSelector(isLoadingSelector);
  const headerRef = useRef();
  const scrollStartMob = useRef(0);
  const scrollStart = useRef(0);
  const toggle = useSelector(visibleHeaderSelector);
  const [toggleWhite] = useState(false);
  const {
    scroll
  } = useLocomotiveScroll();
  const scrollUpdate = useSelector(scrollUpdateSelector);
  const {
    scrollY
  } = useScroll();
  useEffect(() => {
    dispatch(handleVisibleHeader(true));
    const forceShow = () => {
      dispatch(handleVisibleHeader(true));
      if (headerRef.current) headerRef.current.hidden = false;
    };
    const events = ['resize', 'orientationchange'];
    events.forEach(eventName => window.addEventListener(eventName, forceShow));
    return () => {
      events.forEach(eventName_0 => window.removeEventListener(eventName_0, forceShow));
    };
  }, [dispatch]);
  useEffect(() => {
    if (isLoading && ROUTES.root === pathname) {
      setTimeout(() => {
        dispatch(handleVisibleHeader(true));
      }, 2000);
    }
  }, [isLoading, dispatch]);
  useEffect(() => {
    if (!toggle) return;
    if (headerRef.current) headerRef.current.hidden = false;
  }, [toggle]);
  const animatedStyle = useSpring({
    config: {
      duration: 800,
      mass: 1,
      tension: 280,
      friction: 120
    },
    transform: toggle ? 'translateY(0%)' : 'translateY(-100%)',
    delay: 0
  });
  const hidingHeader = () => {
    const current = scrollY;
    if (current > 120) {
      if (current < scrollStart.current) {
        dispatch(handleVisibleHeader(true));
      } else if (current > scrollStart.current) {
        dispatch(handleVisibleHeader(false));
      }
      scrollStart.current = current;
    }
  };
  const hidingHeaderMob = () => {
    const st = window.scrollY;
    if (window.scrollY > 90) {
      if (st > scrollStartMob.current) {
        dispatch(handleVisibleHeader(false));
      } else if (st <= scrollStartMob.current) {
        dispatch(handleVisibleHeader(true));
      }
      scrollStartMob.current = st;
    }
  };
  useEffect(() => {
    if (scroll && !isMobile && pathname !== ROUTES.flats.list) {
      scroll.on('scroll', hidingHeader);
    }
    return () => {
      if (scroll) {
        scroll.off('scroll', hidingHeader);
      }
    };
  }, [scroll, pathname, deviceType, isMobile, scrollUpdate]);
  useEffect(() => {
    if (isMobile || !scroll) {
      window.addEventListener('scroll', hidingHeaderMob);
    }
    return () => window.removeEventListener('scroll', hidingHeaderMob);
  }, [isMobile, scroll, pathname, deviceType, scrollUpdate]);
  useEffect(() => {
    if (pathname !== ROUTES.root) {
      dispatch(handleVisibleHeader(true));
      if (isMobile) {
        window.scrollTo(0, 0);
      }
    } else if (pathname === ROUTES.root && isMobile) {
      window.scrollTo(0, 0);
    }
  }, [deviceType, isMobile]);
  const handleScrollTop = useCallback(() => {
    if (isMobile && pathname === ROUTES.root) {
      dispatch(handleScrollVisible(true));
      window.scrollTo(0, 0);
      setTimeout(() => {
        dispatch(handleScrollVisible(false));
        dispatch(handleVisibleHeader(true));
      }, 500);
    } else if (!isMobile && pathname === ROUTES.root) {
      dispatch(handleScrollVisible(true));
      headerRef.current.hidden = true;
      scroll?.scrollTo(0, 0);
      setTimeout(() => {
        dispatch(handleScrollVisible(false));
        headerRef.current.hidden = false;
        dispatch(handleVisibleHeader(true));
      }, 1500);
    }
  }, [deviceType, pathname, scroll]);
  const chooseContainerColor = useCallback(() => {
    if (headerColorScheme === 'blackOnTransparent' || headerColorScheme === 'blackOnTransparentTwo' || headerColorScheme === 'blackOnTransparentThree') {
      return `${style.headerContainer} ${style.blackOnTransparent}`;
    }
    if (headerColorScheme === 'whiteOnTransparent') {
      return `${style.headerContainer} ${style.whiteOnTransparent}`;
    }
    if (headerColorScheme === 'multiOnTransparent') {
      return `${style.headerContainer} ${style.multiOnTransparent}`;
    }
    return `${style.headerContainer} ${style.headerContainer__white}`;
  }, [toggleWhite, headerColorScheme]);
  const whiteOrBlackContainer = chooseContainerColor();
  const chooseBurgerColor = useCallback(() => {
    if (headerColorScheme === 'whiteOnTransparent') {
      return 'white';
    }
    if (headerColorScheme === '' || headerColorScheme === 'blackOnTransparent') {
      return 'black';
    }
    return 'black';
  }, [headerColorScheme]);
  const burgerColor = chooseBurgerColor();
  return <animated.div data-scroll data-scroll-sticky ref={headerRef} data-scroll-target='.target' className={whiteOrBlackContainer} style={{
    ...animatedStyle
  }}>
      <div className={style.headerBox} itemScope itemType={SCHEMA.WP_HEADER}>
        <meta content='ЖК МОМЕНТС — ламповый проект от девелопера FORMA' itemProp='headline' />
        <meta content='Квартиры в ЖК МОМЕНТС от FORMA: панорамные окна, фасады из натуральных материалов, уютная атмосфера внутри закрытых кварталов.' itemProp='description' />
        <meta content='ЖК МОМЕНТС' itemProp='keywords' />
        {headerColorScheme === 'whiteOnTransparent' ? <HeaderLogo color='white' handleScrollTop={handleScrollTop} /> : <HeaderLogo color='#1C1C1C' handleScrollTop={handleScrollTop} />}

        {!isMobile && <HeaderBar toggleWhite={toggleWhite} headerColorScheme={headerColorScheme} />}
        {burgerColor === 'white' && isMobile && <div className={style.headerBurgerMenu} onClick={() => dispatch(handleOpenMenuMobile(true))} role='button' tabIndex='0'>
            <div className={style.whiteBurger} />
            <div className={style.whiteBurger} />
            <div className={style.whiteBurger} />
          </div>}
        {burgerColor === 'black' && isMobile && <div className={style.headerBurgerMenu} onClick={() => dispatch(handleOpenMenuMobile(true))} role='button' tabIndex='0'>
            <div className={style.blackBurger} />
            <div className={style.blackBurger} />
            <div className={style.blackBurger} />
          </div>}
      </div>
    </animated.div>;
}
export default Header;